import { useEffect, useRef, useState } from 'react';
import { assetsAPI } from '../../api';
import toast from 'react-hot-toast';
import styles from './Assets.module.css';

let _qrPromise = null;
function loadQRScript() {
  if (window.QRCode) return Promise.resolve(window.QRCode);
  if (_qrPromise) return _qrPromise;
  _qrPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    s.onload  = () => resolve(window.QRCode);
    s.onerror = () => reject(new Error('CDN failed'));
    document.head.appendChild(s);
  });
  return _qrPromise;
}
function canvasToBlob(canvas) {
  return new Promise(res => canvas.toBlob(res, 'image/png'));
}

const SIZES = [
  { label:'Small (25x25mm)',  px:110, key:'sm' },
  { label:'Medium (50x30mm)', px:160, key:'md' },
  { label:'Large (75x50mm)',  px:220, key:'lg' },
];

export default function AssetQRModal({ asset, onClose, onSaved }) {
  const qrRef    = useRef(null);
  const [sizeKey, setSizeKey] = useState('md');
  const [copies,  setCopies]  = useState(1);
  const [ready,   setReady]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(!!asset.qr_code);

  const IMG_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api','');
  const qrPx   = SIZES.find(s => s.key === sizeKey)?.px || 160;
  const qrText = `${window.location.origin}/a/${asset.asset_code}`;
  const regDate = asset.purchase_date
    ? new Date(asset.purchase_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})
    : new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});

  useEffect(() => {
    let cancelled = false;
    async function renderQR() {
      if (!qrRef.current) return;
      setReady(false);
      qrRef.current.innerHTML = '';
      try {
        const QRCode = await loadQRScript();
        if (cancelled || !qrRef.current) return;
        qrRef.current.innerHTML = '';
        new QRCode(qrRef.current, {
          text: qrText, width: qrPx, height: qrPx,
          colorDark:'#1e2a3b', colorLight:'#ffffff',
          correctLevel: QRCode.CorrectLevel.M,
        });
        setTimeout(() => { if (!cancelled) setReady(true); }, 150);
      } catch { toast.error('Could not load QR library.'); }
    }
    renderQR();
    return () => { cancelled = true; };
  }, [qrText, qrPx]);
  const getCanvas = () => qrRef.current?.querySelector('canvas');

  const saveToDB = async () => {
    const canvas = getCanvas(); if (!canvas) return;
    setSaving(true);
    try {
      const blob = await canvasToBlob(canvas);
      const r = await assetsAPI.saveQR(asset.id, blob);
      toast.success('QR code saved!'); setSaved(true);
      if (onSaved) onSaved(r.data);
    } catch(e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const downloadPNG = async () => {
    const qrCanvas = getCanvas();
    if (!qrCanvas) return;

    const QR_SIZE  = qrCanvas.width;          // use actual rendered size
    const PAD      = 16;
    const LABEL_H  = 38;                       // extra height for asset code
    const W        = QR_SIZE + PAD * 2;
    const H        = QR_SIZE + PAD * 2 + LABEL_H;

    // Create a new canvas: QR + label
    const canvas = document.createElement('canvas');
    canvas.width  = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // Draw QR centered
    ctx.drawImage(qrCanvas, PAD, PAD, QR_SIZE, QR_SIZE);

    // Asset code below QR (bold, blue, monospace)
    ctx.font      = `bold ${Math.max(13, Math.round(QR_SIZE * 0.095))}px "Courier New", monospace`;
    ctx.fillStyle = '#1a56db';
    ctx.textAlign = 'center';
    ctx.fillText(asset.asset_code, W / 2, QR_SIZE + PAD + 22);

    // Tiny scan hint
    ctx.font      = `${Math.max(9, Math.round(QR_SIZE * 0.065))}px Arial, sans-serif`;
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('Scan to view asset', W / 2, QR_SIZE + PAD + 36);

    const a = document.createElement('a');
    a.download = `QR_${asset.asset_code}.png`;
    a.href     = canvas.toDataURL('image/png');
    a.click();
    toast.success('Downloaded!');
  };
  const printLabel = () => {
    const canvas = getCanvas(); if (!canvas) return;
    const src = canvas.toDataURL('image/png');
    const win = window.open('','_blank','width=520,height=700');
    const card = () => `
      <div class="card">
        <div class="top"><span class="brand">iAssets</span><span class="loc">${asset.location_name||''}</span></div>
        <div class="body">
          <img src="${src}" width="${qrPx}" height="${qrPx}"/>
          <div class="info">
            <div class="name">${asset.name}</div>
            <div class="code">${asset.asset_code}</div>
            <div class="meta">Category: <b>${asset.category_name||'--'}</b> &nbsp; Dept: <b>${asset.department||'--'}</b></div>
            <div class="meta">Registered: <b>${regDate}</b></div>
            <div class="meta">Status: <b class="ok">${(asset.status||'').charAt(0).toUpperCase()+(asset.status||'').slice(1)}</b></div>
          </div>
        </div>
        <div class="footer">SCAN TO VIEW ASSET &nbsp;.&nbsp; ${qrText}</div>
      </div>`;
    win.document.write(`<!DOCTYPE html><html><head><title>QR -- ${asset.asset_code}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Arial,sans-serif;background:#f5f6fa;padding:20px;display:flex;flex-direction:column;gap:16px;align-items:flex-start}
.card{background:#fff;border:1.5px solid #e2e8f0;border-radius:12px;padding:14px 16px;width:420px;page-break-inside:avoid}
.top{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}
.brand{background:#1a56db;color:#fff;font-size:9px;font-weight:900;letter-spacing:.1em;padding:3px 8px;border-radius:4px}
.loc{font-size:11px;color:#64748b}
.body{display:flex;gap:14px;align-items:flex-start}
.info{flex:1}
.name{font-size:18px;font-weight:800;color:#1e2a3b;line-height:1.2;margin-bottom:4px}
.code{font-size:13px;color:#1a56db;font-weight:700;margin-bottom:8px}
.meta{font-size:11px;color:#64748b;margin-bottom:3px}
.ok{color:#16a34a}
.footer{margin-top:10px;padding-top:8px;border-top:1px solid #f1f5f9;font-size:9px;font-weight:600;letter-spacing:.06em;color:#94a3b8;text-align:center}
@media print{body{background:#fff;padding:4px}}</style></head>
<body>${Array.from({length:Number(copies)||1},card).join('')}</body>
<script>window.onload=()=>{setTimeout(()=>window.print(),400);window.onafterprint=()=>window.close()}<\/script>
</html>`);
    win.document.close();
  };

  const statusColor = { operational:'#16a34a', maintenance:'#d97706', breakdown:'#dc2626' };
  const sColor = statusColor[asset.status] || '#64748b';

  return (
    <div className={styles.modal_overlay}>
      {/* Responsive modal: flex column, no max-height overflow hidden -- scroll inside body only */}
      <div style={{
        background:'#fff', borderRadius:16,
        width:'min(520px, 96vw)',
        maxHeight:'92vh',
        display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,0.18)',
      }} onClick={e => e.stopPropagation()}>

        {/* -- HEADER (fixed) -- */}
        <div style={{
          padding:'16px 20px 12px', borderBottom:'1px solid #f1f5f9',
          display:'flex', alignItems:'flex-start', justifyContent:'space-between',
          flexShrink:0,
        }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
              <span style={{ fontSize:16 }}>✅</span>
              <span style={{ fontSize:14, fontWeight:700, color:'#1e2a3b' }}>Asset Registered -- QR Code Ready</span>
            </div>
            <div style={{ fontSize:11, color:'#94a3b8', paddingLeft:24 }}>
              Asset has been saved. Print or download the QR label below.
            </div>
          </div>
          <button onClick={onClose} style={{
            background:'none', border:'none', cursor:'pointer',
            fontSize:18, color:'#94a3b8', padding:'0 2px', lineHeight:1, flexShrink:0,
          }}>✕</button>
        </div>

        {/* -- SCROLLABLE BODY -- */}
        <div style={{ flex:1, overflowY:'auto', padding:'14px 20px', display:'flex', flexDirection:'column', gap:12 }}>

          {/* Green banner */}
          <div style={{
            background:'#f0fdf4', border:'1px solid #bbf7d0',
            borderRadius:10, padding:'10px 14px',
            display:'flex', alignItems:'center', gap:10,
          }}>
            <span style={{ color:'#16a34a', fontSize:14, fontWeight:700 }}>v</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#15803d' }}>
                "{asset.name}" -- {asset.asset_code}
              </div>
              <div style={{ fontSize:11, color:'#22c55e', marginTop:2 }}>
                {[asset.location_name, `Registered ${regDate}`].filter(Boolean).join(' . ')}
              </div>
            </div>
          </div>

          {/* Label preview card */}
          <div style={{
            border:'1.5px solid #e2e8f0', borderRadius:12,
            padding:'14px', background:'#fff',
            boxShadow:'0 2px 8px rgba(0,0,0,.05)',
          }}>
            {/* Top */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <span style={{
                background:'#1a56db', color:'#fff',
                fontSize:9, fontWeight:900, letterSpacing:'.1em',
                padding:'3px 8px', borderRadius:4,
              }}>iAssets</span>
              <span style={{ fontSize:11, color:'#64748b' }}>{asset.location_name||''}</span>
            </div>

            {/* QR + info */}
            <div style={{ display:'flex', gap:14, alignItems:'flex-start', flexWrap:'nowrap', width:'100%' }}>
              <div style={{ flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', alignSelf:'flex-start' }}>
                <div ref={qrRef} style={{
                  width:qrPx,
                  background:'#fff', borderRadius:6, padding:4,
                  border:'1px solid #f1f5f9',
                  display:'flex', alignItems:'flex-start', justifyContent:'center',
                }}/>
                <div style={{
                  fontSize:8, fontWeight:700, letterSpacing:'.07em',
                  color:'#94a3b8', textAlign:'center', marginTop:6, textTransform:'uppercase',
                }}>Scan to view asset</div>
              </div>

              <div style={{ flex:1, minWidth:140, alignSelf:'flex-start', paddingTop:0 }}>
                <div style={{ fontSize:18, fontWeight:800, color:'#1e2a3b', lineHeight:1.2, marginBottom:4 }}>
                  {asset.name}
                </div>
                <div style={{ fontSize:13, color:'#1a56db', fontWeight:700, marginBottom:8 }}>
                  {asset.asset_code}
                </div>
                <div style={{ fontSize:11, color:'#64748b', marginBottom:3 }}>
                  Category: <strong style={{ color:'#1e2a3b' }}>{asset.category_name||'--'}</strong>
                  &nbsp; Dept: <strong style={{ color:'#1e2a3b' }}>{asset.department||'--'}</strong>
                </div>
                <div style={{ fontSize:11, color:'#64748b', marginBottom:3 }}>
                  Registered: <strong style={{ color:'#1e2a3b' }}>{regDate}</strong>
                  &nbsp; Status: <strong style={{ color:sColor }}>
                    {(asset.status||'').charAt(0).toUpperCase()+(asset.status||'').slice(1)}
                  </strong>
                </div>
              </div>
            </div>

            {/* Scan URL */}
            <div style={{ marginTop:10, paddingTop:8, borderTop:'1px solid #f1f5f9', fontSize:9, color:'#94a3b8' }}>
              {qrText}
            </div>
          </div>

          {/* Label size radios */}
          <div style={{ display:'flex', alignItems:'flex-start', gap:8, flexWrap:'wrap' }}>
            <span style={{ fontSize:13, fontWeight:500, color:'#374151', paddingTop:2, minWidth:80 }}>Label Size:</span>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              {SIZES.map(s => (
                <label key={s.key} style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, cursor:'pointer', color:'#374151' }}>
                  <input type="radio" name="qrsize" checked={sizeKey===s.key}
                    onChange={() => setSizeKey(s.key)} style={{ accentColor:'#1a56db' }}/>
                  {s.label}
                </label>
              ))}
            </div>
          </div>

          {/* Copies */}
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:13, fontWeight:500, color:'#374151', minWidth:80 }}>Copies:</span>
            <input type="number" min={1} max={99} value={copies}
              onChange={e => setCopies(Math.max(1,Math.min(99,Number(e.target.value))))}
              style={{ width:64, padding:'6px 10px', border:'1px solid #d1d5db',
                borderRadius:6, fontSize:13, textAlign:'center', outline:'none' }}
            />
            <span style={{ fontSize:12, color:'#94a3b8' }}>(for batch printing)</span>
          </div>

          {/* Saved badge */}
          {saved && (
            <div style={{ padding:'8px 12px', background:'#dcfce7', border:'1px solid #86efac',
              borderRadius:8, fontSize:12, color:'#15803d', fontWeight:600 }}>
              v QR code saved in database
              {asset.qr_code_url && (
                <a href={`${IMG_BASE}${asset.qr_code_url}`} target="_blank" rel="noreferrer"
                  style={{ marginLeft:10, color:'#1a56db', fontSize:11 }}>View saved --</a>
              )}
            </div>
          )}
        </div>

        {/* -- FOOTER (fixed at bottom) -- */}
        <div style={{
          padding:'12px 20px', borderTop:'1px solid #f1f5f9',
          display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end',
          flexShrink:0,
        }}>
          <FBtn onClick={onClose} border="1px solid #d1d5db" bg="#fff" color="#374151">Done</FBtn>
          <FBtn onClick={downloadPNG} disabled={!ready} border="1px solid #d1d5db" bg="#fff" color="#374151">⬇ Download PNG</FBtn>
          <FBtn onClick={saveToDB} disabled={!ready||saving} bg="#16a34a" color="#fff">
            {saving ? '⏳ Saving...' : '💾 Save to DB'}
          </FBtn>
          <FBtn onClick={printLabel} disabled={!ready} bg="#1a56db" color="#fff">🖨 Print Label</FBtn>
        </div>

      </div>
    </div>
  );
}

function FBtn({ onClick, disabled, bg, color, border, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:'8px 16px', borderRadius:8, border:border||'none',
      background: disabled ? '#f1f5f9' : bg,
      color: disabled ? '#94a3b8' : color,
      fontSize:13, fontWeight:600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:6,
    }}>{children}</button>
  );
}
